import {
  isLessThan,
  isNegativeStatus,
  placementStatusOptions,
} from '@AMIEWEB/Placement/NextPlacementStatus/StatusDefaults';
import { AMNDivisionType } from 'app/models/enums/AMNDivisionType';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PlacementDocumentStatus, PlacementDocumentType } from 'store/redux-store/placement-documents/types';
import { selectPlacementDivisionType } from 'store/redux-store/placement-summary/consolidatedStoreSelectors';
import {
  selectPlacementHeaderData,
  selectWageRequestSignedStatus,
} from 'store/redux-store/placement-details/selectors';

export const useAbritrationAgreementButtonEnabled = (currentPlacementStatus, existingDocuments) => {
  const { t } = useTranslation();
  const [tooltipContent, setTooltipContent] = useState([]);
  const placementDivisionType = useSelector(selectPlacementDivisionType);
  const placementHeaderData = useSelector(selectPlacementHeaderData);
  const isPlacementNegativeStatus = isNegativeStatus(currentPlacementStatus);
  const isAgreementAlreadySigned = checkIsExistingAgreementSigned(
    existingDocuments,
    placementHeaderData,
  );
  const isNonEligibleDivision =
    placementDivisionType === AMNDivisionType.inDemand ||
    placementDivisionType === AMNDivisionType.intl ||
    placementDivisionType === AMNDivisionType.localStaffing;

  useEffect(() => {
    const tooltipArray = [];
    if (isNonEligibleDivision) {
      tooltipArray.push(t('placement.arbitrationAgreementBtn.nonEligibleDivisionTooltip'));
    }
    if (isPlacementNegativeStatus) {
      tooltipArray.push(t('placement.arbitrationAgreementBtn.negativeStatusTooltip'));
    }
    if (isAgreementAlreadySigned) {
      tooltipArray.push(t('placement.arbitrationAgreementBtn.alreadyAgreementSigned'));
    }
    setTooltipContent(tooltipArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonEligibleDivision, isPlacementNegativeStatus, isAgreementAlreadySigned]);
  return {
    isEnabled: tooltipContent?.length === 0 && existingDocuments !== undefined,
    tooltipContent,
  };
};
export const useWageRequestFormButtonEnabled = (currentPlacementStatus, existingDocuments) => {
  const allowdStates = ['CA', 'WA', 'DC', 'NY'];
  const { t } = useTranslation();
  const [tooltipContent, setTooltipContent] = useState([]);
  const placementDivisionType = useSelector(selectPlacementDivisionType);
  const placementHeaderData = useSelector(selectPlacementHeaderData);
  const wageRequestSignedStatus = useSelector(selectWageRequestSignedStatus);
  const isPlacementNegativeStatus = isNegativeStatus(currentPlacementStatus);
  const isLessThanBookingApproved = isLessThan(
    currentPlacementStatus,
    placementStatusOptions.bookingApproved.activityStatusId,
  );
  const isValidState = allowdStates.includes(placementHeaderData?.facility?.state);
  const isAgreementAlreadySigned = wageRequestSignedStatus;
  const isNonEligibleDivision = !(
    placementDivisionType === AMNDivisionType.allied ||
    placementDivisionType === AMNDivisionType.amnNurse ||
    placementDivisionType === AMNDivisionType.hsg
  );

  useEffect(() => {
    const tooltipArray = [];
    if (isAgreementAlreadySigned) {
      tooltipArray.push(t('placement.wageRequestButton.toolTip.signedWageRequest'));
    }
    if (isPlacementNegativeStatus || isLessThanBookingApproved) {
      tooltipArray.push(t('placement.wageRequestButton.toolTip.invalidPlacementStatus'));
    }
    if (isNonEligibleDivision) {
      tooltipArray.push(t('placement.wageRequestButton.toolTip.nonEligibleDivision'));
    }
    if (!isValidState) {
      tooltipArray.push(t('placement.wageRequestButton.toolTip.invalidState'));
    }
    setTooltipContent(tooltipArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonEligibleDivision, isPlacementNegativeStatus, isAgreementAlreadySigned, isLessThanBookingApproved]);
  return {
    isEnabled: tooltipContent?.length === 0,
    tooltipContent,
  };
};

const checkIsExistingAgreementSigned = (existingDocuments, placementHeaderData) => {
  const arbitrationAgreementDocs = existingDocuments?.filter(
    doc => doc?.documentType?.toLowerCase() === PlacementDocumentType.arbitrationAgreement && doc?.legalEntityId === placementHeaderData?.legalEntityId
  );

  return arbitrationAgreementDocs?.some(doc => doc?.status === PlacementDocumentStatus.signed) ?? false;
};